import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import Card from "../components/Card";
import TrustedBy from "../components/TrustedBy";
import SEO from "../components/SEO";
import StructuredData, {
  createServiceSchema,
  createBreadcrumbSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

// Viewport settings for better animation triggering
const viewportSettings = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -50px 0px",
};

const InvoiceHandling = () => {
  const traditionalIssues = [
    "Collect invoices from all vendors and suppliers, often in different formats and qualities.",
    "Manually extract relevant information from all documents and prepare a report in Excel.",
    "Share the report with project financiers or partners for disbursement of funds.",
    "Use the report to calculate project spendings and forecast cash flow.",
  ];

  const aiSolutions = [
    "Automate invoice processing and data extraction from various vendors and suppliers.",
    "Minimize errors and save time by eliminating manual data entry.",
    "Get paid for the completed work, faster.",
    "Streamline the invoicing process to efficiently manage project finances.",
  ];

  const steps = [
    {
      number: 1,
      title: "Upload or email invoices",
      description:
        "Easily upload invoices directly to the platform or simply send them via email. Our tool supports multiple file formats, making it convenient for you to submit documents in various forms.",
      image: "/invoice-handling/step-1.png",
    },
    {
      number: 2,
      title: "Estabild AI analyzes files",
      description:
        "Once your invoices are received, our advanced AI technology quickly analyzes all files, extracting key data points and organizing the information.",
      image: "/invoice-handling/step-2.png",
    },
    {
      number: 3,
      title: "Get instant summary",
      description:
        "Within moments, you'll receive a comprehensive summary of the analyzed invoices. It provides clear insights into your financial transactions, empowering you to efficiently manage project finances.",
      image: "/invoice-handling/step-3.png",
    },
  ];

  const invoiceSeo = seoData.invoiceHandling;
  const structuredData = [
    createServiceSchema(
      "Invoice Handling & Management",
      invoiceSeo.description,
      invoiceSeo.url
    ),
    createBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Solutions", url: "/#solutions" },
      { name: "Invoice Handling", url: invoiceSeo.url },
    ]),
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO
        title={invoiceSeo.title}
        description={invoiceSeo.description}
        keywords={invoiceSeo.keywords}
        url={invoiceSeo.url}
      />
      <StructuredData data={structuredData} />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="text-center mb-8">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                AI-powered invoices handling for development projects
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-5xl mx-auto">
                Enable your team to efficiently manage project finances and
                focus on value-adding activities.
                <br />
                We'll take care of the boring stuff that no one likes.
              </p>
              <div className="mt-8">
                <Link to="/contact">
                  <Button variant="primary" size="large">
                    Get access
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-12"
            >
              <img
                src="/invoice-handling/hero-image.png"
                alt="AI Invoice Processing"
                className="w-full"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section */}
      <TrustedBy />

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
              What used to take countless hours
            </h2>
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
              now can be done in minutes
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Traditional Workflow */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportSettings}
              transition={{ duration: 0.6 }}
            >
              <Card>
                <h3 className="text-sm uppercase tracking-wider text-red-600 font-bold mb-6">
                  TRADITIONAL WORKFLOW
                </h3>
                <p className="text-gray-600 mb-6">
                  Reviewing your project's financials can be overwhelming. As a
                  key stakeholder you need to know the numbers that are most
                  relevant to your business bottom-line.
                </p>
                <div className="space-y-4">
                  {traditionalIssues.map((issue, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <img
                        src="/funding/x-icon.svg"
                        alt="Issue"
                        className="w-6 h-6 flex-shrink-0 mt-1"
                      />
                      <p className="text-gray-600">{issue}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* AI-Powered Solution */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={viewportSettings}
              transition={{ duration: 0.6 }}
            >
              <Card className="bg-gradient-to-br from-primary-50 to-white border-primary-200">
                <h3 className="text-sm uppercase tracking-wider text-primary-600 font-bold mb-6">
                  AI-POWERED SOLUTION
                </h3>
                <p className="text-gray-600 mb-6">
                  Reviewing your project's financials can be overwhelming. As a
                  key stakeholder you need to know the numbers that are most
                  relevant to your business bottom-line.
                </p>
                <div className="space-y-4">
                  {aiSolutions.map((solution, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <img
                        src="/funding/check-icon.svg"
                        alt="Solution"
                        className="w-6 h-6 flex-shrink-0 mt-1"
                      />
                      <p className="text-gray-600">{solution}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
              Works in 3 simple steps
            </h2>
          </motion.div>

          <div className="space-y-20">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportSettings}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } gap-12 items-center`}
              >
                <div className="flex-1">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full rounded-lg shadow-lg bg-gray-100"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {step.number}. {step.title}
                  </h3>
                  <p className="text-gray-600 text-lg">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Focus on your projects growth.
              <br />
              We'll take care of the reporting.
            </h2>
            <Link to="/contact">
              <Button variant="ghost" size="large">
                Book demo
              </Button>
            </Link>
            <div className="mt-12">
              <p className="text-sm uppercase tracking-wider text-primary-200 mb-2">
                Email
              </p>
              <a
                href="mailto:hello@estabild.com?subject=Interest%20in%20Estabild"
                className="text-2xl font-semibold hover:text-primary-100 transition-colors"
              >
                hello@estabild.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InvoiceHandling;
